import { createServerFn } from "@tanstack/react-start";
import { LogtoScopes } from "~/lib/logto/logto";
import {
  listOrganizationsWithProduct,
  type EntitlementsOrg,
} from "~/lib/entitlements/admin-api";
import { requireAuth, requireScopes, rateLimitFunction } from "~/lib/middleware";
import type { Organization } from "~/lib/schemas";
import { filterOrgsImpl } from "./filterOrgs";

// Where a row came from. Entitlements is authoritative for who may use the
// portal; DaaS holds the keys, quotas and usage. `entitlements` rows are the
// ones the sync action exists for; `daas` rows are orgs DaaS knows that hold no
// dde product — legacy or hand-made, listed so nothing silently vanishes from
// this table.
export type AdminOrgSync = "synced" | "entitlements" | "daas";

export interface AdminOrgRow {
  id: string;
  name: string;
  sync: AdminOrgSync;
  entitlements: EntitlementsOrg | null;
  daas: Organization | null;
}

export interface AdminOrgList {
  rows: AdminOrgRow[];
  // Set when Entitlements could not be reached. The list then holds DaaS's orgs
  // alone, which is a different list from the authoritative one — every row's
  // sync state would otherwise read as "not in Entitlements", and quota and key
  // management for orgs that do exist would be unreachable during an outage.
  // The page says so rather than quietly showing the smaller list.
  entitlementsError: string | null;
}

export const listAdminOrgs = createServerFn({
  method: "GET",
})
  .middleware([requireAuth, rateLimitFunction, requireScopes(LogtoScopes.AdminOrgRead)])
  .handler(async (): Promise<AdminOrgList> => {
    // Both sides in parallel: neither is derivable from the other, and the
    // reconciliation is the whole point of the list.
    const [entitlementsResult, daasOrgs] = await Promise.all([
      listOrganizationsWithProduct().then(
        (orgs) => ({ orgs, error: null as string | null }),
        (error: unknown) => {
          console.error("Failed to list Entitlements organizations:", error);
          return {
            orgs: [] as EntitlementsOrg[],
            error:
              error instanceof Error ? error.message : "Entitlements is unreachable",
          };
        },
      ),
      filterOrgsImpl({ sortOrder: "asc" }),
    ]);

    const entitlementsOrgs = entitlementsResult.orgs;

    // DaaS uses the Logto id AS its organization id whenever the org was
    // created from Logto (source LOGTO), which is what makes this a plain id
    // match rather than a join through a mapping column.
    const daasById = new Map(daasOrgs.map((org) => [org.id, org]));
    const rows: AdminOrgRow[] = [];

    for (const org of entitlementsOrgs) {
      const daas = daasById.get(org.id) ?? null;
      daasById.delete(org.id);
      rows.push({
        id: org.id,
        name: org.name,
        sync: daas ? "synced" : "entitlements",
        entitlements: org,
        daas,
      });
    }

    for (const daas of daasById.values()) {
      rows.push({
        id: daas.id,
        name: daas.name,
        // With Entitlements down every DaaS org would look like an orphan, so
        // the leftovers are reported as synced and the banner carries the news.
        sync: entitlementsResult.error ? "synced" : "daas",
        entitlements: null,
        daas,
      });
    }

    return { rows, entitlementsError: entitlementsResult.error };
  });
